/**
 * Version string (YYYY-MM-DD or YYYY-MM-DD.N for same-day revision) when the LevAI agent definition was last updated (system prompt, persona, canvas_behavior).
 * Bump this when those change. Projects with levaiAgentVersion earlier than this (string compare) see the "Get the latest version" CTA.
 * Stored on the project so we can tell which version each agent is running.
 */
export const LEVAI_AGENT_VERSION = "2026-10-30.3";

/** Latency-first default "none" (7.2s per agentic turn vs 16.1s at "low"); LEVAI_CHAT_MODEL_REASONING opts into more. */
export const LEVAI_DEFAULT_REASONING_EFFORT: import("@/lib/ai/gpt5").Gpt5ReasoningEffort = "none";

/**
 * Sentinel User row id used as the author for Lev-authored comments.
 * Seeded by migration 20260429111333_block_comments_phase_1. Treated specially
 * in the UI to render with Lev's avatar/branding rather than as a normal user.
 */
export const LEV_USER_ID = "system:lev";

/**
 * Changelog entries for "what's new" shown after upgrade. Oldest first; add a new entry whenever you bump LEVAI_AGENT_VERSION.
 * Users who are several versions behind see all entries from their version up to current, with the date (YYYY-MM-DD) of each.
 */
export const LEVAI_WHATS_NEW_ENTRIES: { version: string; summary: string }[] = [
  {
    version: "2026-03-01",
    summary:
      "Improved handling of document feedback and clearer suggestions when you have multiple options in a block.",
  },
  {
    version: "2026-03-03.1",
    summary:
      "I now better understand short cues like \"Looks good\" or \"Let's keep going\" and will approve or continue for you when you're viewing a draft. I'll also keep replies short and better navigate the canvas for you. Finally, I'll respond with better suggestions when you have multiple options in a particular block.",
  },
  {
    version: "2026-03-10",
    summary:
      "When you ask me to add a task, I'll now show it with Accept/Reject buttons so you can review before it's added to your Weekly Focus.",
  },
  {
    version: "2026-03-10.1",
    summary:
      "For the Prospects block I can add or edit one prospect at a time when you ask. Use the Add button in the header to add manually, or ask me to add or update prospects and I'll use the right tools.",
  },
  {
    version: "2026-03-10.2",
    summary:
      "Task proposal feature with Accept/Reject buttons is now integrated with latest Prospects features.",
  },
  {
    version: "2026-03-18",
    summary:
      "When you want to add a custom task, I'll now ask you to explain it in 3-4 lines so I can set it up properly.",
  },
  {
    version: "2026-03-25",
    summary:
      "Fixed task creation flow: I'll now show a proposal card with Accept/Reject buttons before creating tasks.",
  },
  {
    version: "2026-03-25.1",
    summary:
      "Tasks you add via chat now automatically classify to the right step with structured details and P1 priority.",
  },
  {
    version: "2026-04-08",
    summary:
      "I now use a dedicated tool to populate the suggestion chips below the chat, so you'll never see raw JSON leak into my replies. I can also edit tasks you're viewing directly — just tell me what to change (target, steps, things to look for, etc.). Custom tasks I create for you go straight into Weekly Focus with no approval step in between. And I create deliverables more reliably by passing the exact step and deliverable instead of guessing from titles.",
  },
  {
    version: "2026-04-17.4",
    summary:
      "When you ask me for options or alternatives, I now present them as clickable buttons you can pick from. My follow-up suggestions are smarter too.",
  },
  {
    version: "2026-04-23",
    summary:
      "I'm better at finding content when you reference something in a different section — I'll search across your blocks instead of giving up. I also make edits directly now when you ask for changes, so you can Accept or Reject right away.",
  },
  {
    version: "2026-04-24",
    summary:
      "When you ask me for options or to help you choose, I now always show clickable buttons instead of writing them out as text. Picking an option is one tap instead of typing.",
  },
  {
    version: "2026-04-28",
    summary:
      "Ask me about your YC application and I'll read it directly — your score, your weakest section, the biggest gap a YC partner would notice. Say 'take me to my YC app' and I'll open the coaching panel for you.",
  },
  {
    version: "2026-04-29",
    summary:
      "I can now leave a comment on a specific block when something's worth following up on outside of chat — and tag you so you get a notification. Try asking 'leave a comment on the TAM block reminding me to revisit the bottom-up model.'",
  },
  {
    version: "2026-05-05",
    summary:
      "Comments on the canvas are now between you and your project collaborators. I focus on the chat side of things.",
  },
  {
    version: "2026-05-13",
    summary:
      "I'm faster now — responses arrive quicker and canvas events like opening a block feel snappier.",
  },
  {
    version: "2026-05-20",
    summary:
      "If I'm ever suggesting you revisit something based on a previous insight, you can now just tell me you're keeping it as-is in chat — I'll stop bringing it up unless something new comes up later.",
  },
  {
    version: "2026-05-20.1",
    summary:
      "When you finish a task, just tell me — \"we updated competitive,\" \"I'm done with pricing,\" or even just \"what's next?\" after you've done the work. I'll mark it complete and keep your Weekly Focus moving. You can always undo from the sidebar if I read the signal wrong.",
  },
  {
    version: "2026-05-22",
    summary:
      "I can now help you build a People DB — track contacts, Deals, Applications, and Customer Discovery calls. Search your existing contacts, add new people and companies, move them through pipeline stages, and write notes. A single Deal or Application can attach multiple people (e.g. a deal team or a hiring panel) with one promotable primary. Click through related records inside the side panel — a back arrow walks you back to where you started. Open any block on the canvas to get started.",
  },
  {
    version: "2026-06-01",
    summary:
      "I can now launch research sandboxes to browse the web and conduct deeper competitive analysis, market research, market sizing, and industry investigation autonomously.",
  },
  {
    version: "2026-06-02",
    summary:
      "I no longer track manual effort estimates on tasks — every task I create is sized to fit in a single sitting, and longer goals will be broken into a sequence of tasks for you.",
  },
  {
    version: "2026-06-02.2",
    summary:
      "I now generate your weekly tasks from a structured playbook for the rung you're on — each task is anchored to a specific outcome and grouped into Quests when an artifact takes more than one sitting. When you finish the work that closes a rung, I'll move you to the next one automatically.",
  },
  {
    version: "2026-06-03",
    summary:
      "Your People DB now focuses on two pipelines: Customers (customer discovery) and Applicants (hiring). I'll help you track contacts, move them through stages, and write notes in each.",
  },
  {
    version: "2026-06-09",
    summary:
      "I'm more thoughtful about which tools to use and when — I'll read your content before editing it, double-check before approving, and follow through on multi-step requests without stopping halfway.",
  },
  {
    version: "2026-06-09.1",
    summary:
      "The research sandbox now supports code execution -- I can write and run scripts to do statistical analysis, parse web content, build comparison tables, and crunch numbers for market sizing and financial projections.",
  },
  {
    version: "2026-06-10",
    summary:
      "Applicants is taking a short break -- for now your People DB focuses on Customers (customer discovery). Applicants will return as a dedicated feature later.",
  },
  {
    version: "2026-06-10",
    summary:
      "When we kick off deep research from chat, the results now land directly on the right canvas block as a proposed edit you can accept or refine. We also improved the live activity panel so you can follow every step of the research in real time.",
  },
  {
    version: "2026-06-18",
    summary:
      "If I check in on a task you already finished — say a few days later — and you tell me how it went, I'll save that reflection to the task automatically. Your Weekly Focus and weekly report will show your notes the next time they update.",
  },
  {
    version: "2026-06-19",
    summary:
      "When you pick an option from multiple choices (like choosing your ICP persona), I now propose an edit to consolidate the block around your choice instead of just acknowledging it in chat.",
  },
  {
    version: "2026-07-16",
    summary:
      "I've been upgraded to a newer, faster model. Chat replies should feel noticeably snappier, and I'll be more diligent about remembering the details you share with me.",
  },
  {
    version: "2026-07-21",
    summary:
      "Your journey is no longer locked to a fixed order — ask me for any section whenever you want it (say, Product Build before Market Size) and I'll create it right away. If key context is missing I might ask a question or two first, and my recommendations now follow where you actually are, not a checklist.",
  },
  {
    version: "2026-07-28",
    summary:
      "Lev now understands your startup stage and recommends what to work on next based on where it will have the most impact. When you ask what to do next, we will lead with a specific suggestion tailored to your stage and progress rather than an open-ended question.",
  },
  {
    version: "2026-07-28.1",
    summary:
      "Lev is clearer about exactly what it can build for you and won't over-promise. If what you tell us about your stage doesn't match what your deck or site shows, Lev will check in conversationally instead of assuming — one quick question, then we keep moving.",
  },
  {
    version: "2026-07-28.2",
    summary:
      "Whenever Lev recommends what to work on next, it now explains its thinking in plain language — the evidence it's weighing and what the work unblocks — so you can judge the recommendation instead of taking it on faith.",
  },
  {
    version: "2026-07-28.3",
    summary:
      "When your Customer Discovery lays out several candidate customers, Lev now nudges you to commit to one before going deeper — with its recommended pick and reasoning — because a chosen focus multiplies the value of everything built after it.",
  },
  {
    version: "2026-07-29.1",
    summary:
      "Lev's spoken recommendation and the buttons on your canvas now always agree: both come from the same ranked priority, and Lev explains the reasoning behind it instead of second-guessing it. Lev also refers to every section by the name you see on the canvas.",
  },
  {
    version: "2026-07-29.2",
    summary:
      "Lev's recommendation for what to work on next now always reflects your canvas exactly as it is right now — the moment you build or approve a section, Lev's next suggestion moves on with you, with no lag.",
  },
  {
    version: "2026-07-30",
    summary:
      "When you finish reviewing a section, Lev now shows approval and improvement options right in the document viewer instead of a dialog after you close it. Lev can also create tasks for real-world actions you need to take (like conducting customer interviews) and will track them in your Weekly Focus.",
  },
  {
    version: "2026-07-30.1",
    summary:
      "Lev now stays focused on one thing at a time: while a draft is in front of you for review, Lev helps you finish that review before suggesting what to work on next.",
  },
  {
    version: "2026-07-31",
    summary:
      "We fixed an issue where Lev could fail to respond in chat after our latest model upgrade. Every project agent now runs on refreshed model settings.",
  },
  {
    version: "2026-08-03",
    summary:
      "Lev now gets to know you during onboarding — share your LinkedIn or personal site and Lev builds a richer picture of your background to personalize its advice from day one.",
  },
  {
    version: "2026-08-06",
    summary:
      "Choosing your ICP is now one reliable step: Lev points you to your Customer Discovery block, where you tap the customer you want to focus on. That single tap locks in your ICP and unlocks market sizing, go-to-market, and everything downstream.",
  },
  {
    version: "2026-08-07",
    summary:
      "Ask me what a term means and you'll get the reviewed definition from Lev's concept library rather than my own paraphrase. Same explanation you'd find on the concept's page, tied back to your company. If a term isn't in the library yet, I'll just answer normally.",
  },
  {
    version: "2026-08-11",
    summary:
      "When I continue your flow or generate the next section, I now build exactly the next step I recommend to you — what I say and what your canvas suggests always agree. One source of truth, one answer.",
  },
  {
    version: "2026-08-14",
    summary:
      "I now keep your founder profile within its safe memory limit while preserving the identity and enrichment details that help me remember your context.",
  },
  {
    version: "2026-08-16",
    summary:
      "I can now talk about your fundraising lane the same way I talk about the rest of the canvas — raise brief, funding climate, comparable raises, pitch deck, and investor targets.",
  },
  {
    version: "2026-08-18",
    summary:
      "When you choose a customer to focus on, Lev now carries that decision forward reliably across your canvas and conversations.",
  },
  {
    version: "2026-08-31",
    summary:
      "When something you ask for needs a free account — like jumping ahead to a later section, opening your People list, or sharing your project — I'll now tell you straight and point you to signing up, instead of saying I did it when I couldn't.",
  },
  {
    version: "2026-09-01",
    summary:
      "The Fundraising lane is now a guided wizard — Lev acts as a read-only co-pilot while you work through it, explaining each step without proposing edits or offering suggestion chips.",
  },
  {
    version: "2026-09-01.1",
    summary:
      "I now describe Fundraising the way it actually works: tap Start on your Fundraising panel and I'll walk you through it step by step. I'll no longer ask you to approve a fundraising draft, because there isn't one to approve.",
  },
  {
    version: "2026-09-02",
    summary:
      "Customer discovery now focuses on your ideal customer profile and pain points, with sharper research tasks to match.",
  },
  {
    version: "2026-09-03",
    summary:
      "My next-step suggestion and the orange button now always agree. I'll point you at the oldest draft still waiting for your review, and tell you why it matters.",
  },
  {
    version: "2026-09-03.1",
    summary:
      "My suggested next steps now come only from your canvas, so every chip maps to a real section — no more invented shortcuts that lead nowhere.",
  },
  {
    version: "2026-09-08.1",
    summary:
      "I now repeat exactly how a follow-up draft is queued instead of paraphrasing it.",
  },
  {
    version: "2026-09-09",
    summary:
      "I now keep your stage and fundraising context in mind on every turn, so my suggestions and the reasons behind them stay grounded in where you actually are.",
  },
  {
    version: "2026-09-09.1",
    summary:
      "Before you have an account, I now know exactly which abilities a free account unlocks — editing, evaluations, tasks, and more — so I can offer to unlock them instead of pretending an action happened.",
  },
  {
    version: "2026-09-09.2",
    summary:
      "When you ask for something that needs a free account — like adding a task — I now point you straight to signing up on the spot, with no clarifying questions first.",
  },
  {
    version: "2026-09-09.3",
    summary:
      "When you ask to build a section, I just build it — I no longer second-guess the order or claim a section is out of sequence when it isn't.",
  },
  {
    version: "2026-09-15",
    summary:
      "When we're talking about one of your tasks, you can now ask me to run it for you. I'll do the work and open it for your review. You can also ask me to pause it to set it aside until you're ready to pick it back up.",
  },
  {
    version: "2026-09-21",
    summary:
      "\"Work on X\" on a preview project now generates the section instead of asking for an account — only a direct command to rewrite or change an existing section requires sign-up.",
  },
  {
    version: "2026-09-23",
    summary:
      "I now update my notes on each part of your venture in place as I learn more, so my advice stays grounded in the details you've shared.",
  },
  {
    version: "2026-10-09",
    summary:
      "I now call your customer pipeline by the name your canvas shows it under, so we're always talking about the same board.",
  },
  {
    version: "2026-10-20.1",
    summary:
      "Your YC application now gets a score and coaching on every answer, so you can see which ones to strengthen first.",
  },
  {
    version: "2026-10-27",
    summary:
      "You can now tell me your ICP in your own words, and I'll work material you paste into your canvas instead of sending you elsewhere.",
  },
  {
    version: "2026-10-30.1",
    summary:
      "I now keep notes on every part of your venture, from your big idea to your traction. When you tell me something new, like your customer count or pricing, I save it in the right place so my advice draws on it.",
  },
  {
    version: "2026-10-30.2",
    summary: "My working instructions are now locked, so I can't accidentally rewrite how I help you.",
  },
  {
    version: "2026-10-30.3",
    summary:
      "I now keep my notes about you and your venture in view on every message, so I stop losing track of what you've told me.",
  },
];

// This is an override_system turn, which replaces the compiled system message
// and drops every memory block (founder_identity, the step capture blocks,
// persona, canvas_behavior, …) for its one call. That is the ONE place Lev
// turns are allowed to do this: the turn neither replies nor calls a tool, so
// there is nothing memory-less output could get wrong. Every other LevAI
// turn (chat, event, comment) must keep its memory blocks intact.
export const LEVAI_FAST_PATH_SYSTEM_NO_REPLY =
  "You are a canvas assistant. A system event was recorded. Do not reply and do not call any tool.";

// Friendly prompts sent to LevAI (implicit meaning) — used by flow chips and sendMessage
export const APPROVE_AND_CONTINUE_PROMPT = "This looks good to me";
export const CONTINUE_TO_NEXT_SECTION_PROMPT = "Let's keep going";
export const CONTINUE_WHERE_LEFT_OFF_PROMPT = "Continue where I left off";

/**
 * Single message string for chat: what's new for every version after fromVersion up to LEVAI_AGENT_VERSION.
 * Each version is shown with its date (YYYY-MM-DD from the version string) so users catching up across many versions see a clear timeline.
 */
export function formatWhatsNewSince(fromVersion: string): string {
  const entries = LEVAI_WHATS_NEW_ENTRIES.filter(
    (e) => e.version > fromVersion && e.version <= LEVAI_AGENT_VERSION,
  ).sort((a, b) => a.version.localeCompare(b.version));

  if (entries.length === 0) return "";
  if (entries.length === 1) {
    const dateLabel = entries[0]!.version.slice(0, 10);
    return `**${dateLabel}** — ${entries[0]!.summary}`;
  }
  return entries
    .map((e) => {
      const dateLabel = e.version.slice(0, 10);
      return `**${dateLabel}** — ${e.summary}`;
    })
    .join("\n\n");
}
